import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Gift, Leaf, X } from "lucide-react";
import { useHoliday, THEME_STYLE } from "../../lib/holiday";

const SEEN = "fff_holiday_popup_seen";
const CARD_OFF = "fff_holiday_card_off";
const read = (k) => { try { return sessionStorage.getItem(k); } catch { return null; } };
const write = (k, v) => { try { sessionStorage.setItem(k, v); } catch { /* private mode */ } };

const Popup = ({ h, s, Icon, onClose }) => (
  <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4 animate-in fade-in" role="dialog" aria-modal="true" data-testid="holiday-popup">
    <div className={`relative w-full max-w-md rounded-3xl ${s.soft} shadow-2xl p-7 text-center animate-in zoom-in-95`}>
      <button type="button" onClick={onClose} aria-label="Close" data-testid="holiday-popup-close" className="absolute top-3 right-3 inline-flex h-9 w-9 items-center justify-center rounded-full text-brand-charcoal/50 hover:bg-black/5 transition-colors">
        <X className="h-4 w-4" />
      </button>
      <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-white shadow" style={{ color: s.accent }}>
        <Icon className="h-7 w-7 animate-[holiday-sway_3s_ease-in-out_infinite]" />
      </span>
      <h2 className="mt-4 font-display font-black text-[26px] leading-tight text-brand-charcoal">{h.popup_title}</h2>
      <p className="mt-2 text-[14px] text-brand-charcoal/70">{h.popup_text}</p>
      <Link to="/holiday" onClick={onClose} data-testid="holiday-popup-cta" className={`mt-6 inline-flex items-center gap-2 rounded-full px-6 py-3.5 text-[12px] font-bold uppercase tracking-[0.12em] transition-colors ${s.button}`}>
        {h.cta_label || "See deals"} <ArrowRight className="h-4 w-4" />
      </Link>
      <button type="button" onClick={onClose} data-testid="holiday-popup-later" className="mt-3 block mx-auto text-[12px] text-brand-charcoal/50 hover:text-brand-charcoal">Maybe later</button>
    </div>
  </div>
);

const Card = ({ h, s, Icon, onClose }) => (
  <div data-testid="holiday-card" className="fixed bottom-24 sm:bottom-6 left-4 z-[45] w-[260px] rounded-2xl bg-white shadow-xl border border-black/5 p-4 animate-in slide-in-from-bottom-4">
    <button type="button" onClick={onClose} aria-label="Hide" data-testid="holiday-card-close" className="absolute top-2 right-2 text-brand-charcoal/40 hover:text-brand-charcoal"><X className="h-3.5 w-3.5" /></button>
    <p className={`flex items-center gap-1.5 text-[10.5px] font-black uppercase tracking-[0.14em] ${s.accentText}`}><Icon className="h-3.5 w-3.5" /> Holiday deals</p>
    <p className="mt-1 font-bold text-[14px] leading-snug text-brand-charcoal pr-4">{h.popup_title}</p>
    <Link to="/holiday" data-testid="holiday-card-cta" className={`mt-3 inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[11px] font-bold uppercase tracking-[0.1em] transition-colors ${s.button}`}>
      {h.cta_label || "See deals"} <ArrowRight className="h-3.5 w-3.5" />
    </Link>
  </div>
);

// Pop-up once per visit (home page), then a small corner card on public pages.
export const HolidayPromo = () => {
  const h = useHoliday();
  const { pathname } = useLocation();
  const [popup, setPopup] = useState(false);
  const [card, setCard] = useState(() => !!read(SEEN) && !read(CARD_OFF));
  const hiddenHere = pathname.startsWith("/admin") || pathname === "/holiday" || pathname.startsWith("/invoice") || pathname.startsWith("/events/join");

  useEffect(() => {
    if (!h || read(SEEN) || pathname !== "/") return undefined;
    const t = setTimeout(() => setPopup(true), 4000);
    return () => clearTimeout(t);
  }, [h, pathname]);

  if (!h || hiddenHere) return null;
  const s = THEME_STYLE[h.active];
  const Icon = h.active === "christmas" ? Gift : Leaf;
  const closePopup = () => { write(SEEN, "1"); setPopup(false); setCard(true); };
  const closeCard = () => { write(CARD_OFF, "1"); setCard(false); };
  if (popup) return <Popup h={h} s={s} Icon={Icon} onClose={closePopup} />;
  return card ? <Card h={h} s={s} Icon={Icon} onClose={closeCard} /> : null;
};
